import React from "react";
import { Helmet } from "react-helmet-async";
import { BookOpen, CalendarDays, ExternalLink, Globe, Landmark, MapPin, Receipt, ShieldCheck } from "lucide-react";
import { COURSE_DETAILS } from "../../data/courseDetails";
import { fillTokens, getCourseFacts, LAST_UPDATED } from "../../data/courseFacts";
import { LANGUAGES } from "../../data/courseGroups";
import { CONTACT, PARTNER_VENUES, resolveVenue, VOTRA_LOCATIONS } from "../../data/votraLocations";
import { courseJsonLd, faqJsonLd, SITE_URL } from "../Seo";

const INK = "hsl(222.2, 47.4%, 11.2%)";
const BRAND = "hsl(210,100%,45%)";

export const kr = (n) =>
  typeof n === "number" && n > 0
    ? n.toLocaleString("da-DK", { maximumFractionDigits: 0 }) + " kr."
    : null;

/** Alle faktuelle tal ét sted, så faktaboks, tekst, FAQ og schema aldrig kan afvige. */
export const buildCourseFactModel = (group, sessions = []) => {
  const facts = getCourseFacts(group.slug);
  const da = group.prices?.da?.standard ?? group.prices?.da;
  const fremmed = group.prices?.fremmedsprog?.standard ?? group.prices?.fremmedsprog;
  const babPrices = group.hasTypeVariants
    ? Object.values(group.prices || {}).map((t) => t.price).filter(Boolean)
    : [];
  const low = group.hasTypeVariants ? Math.min(...babPrices) : (typeof da === "number" ? da : null);
  const high = group.hasTypeVariants
    ? Math.max(...babPrices)
    : (typeof fremmed === "number" ? fremmed : low);

  const upcoming = (sessions || [])
    .filter((s) => s.date)
    .slice()
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const languages = [...new Set(upcoming.map((s) => s.languageName).filter(Boolean))];
  const venues = [...new Set(upcoming.map((s) => s.location).filter(Boolean))];

  const tokens = {
    pris: kr(low) || "-",
    prisFremmedsprog: kr(high) || kr(low) || "-",
    varighed: group.duration || "",
  };

  return { facts, low, high, da, fremmed, upcoming, languages, venues, tokens };
};

const Row = ({ label, children, testid }) => (
  <div className="flex flex-wrap gap-x-3 gap-y-0.5 py-2 border-b border-slate-100 last:border-0" data-testid={testid}>
    <dt className="w-full sm:w-44 shrink-0 text-sm text-slate-500">{label}</dt>
    <dd className="flex-1 min-w-0 break-words text-sm font-medium text-slate-800">{children}</dd>
  </div>
);

/**
 * Faktaboks, definition, omkostninger, prøve og myndighedskilder — den fælles
 * informationsmodel for alle Votras kursussider (masterstandard).
 */
export const CourseFactsSections = ({ group, sessions }) => {
  const model = buildCourseFactModel(group, sessions);
  const { facts, tokens, upcoming, languages, venues } = model;
  if (!facts) return null;

  const details = COURSE_DETAILS[group.slug] || {};
  const next = upcoming[0];
  const partnerVenue = venues.some((v) => PARTNER_VENUES.some((p) => (v || "").toLowerCase().includes(p.city.toLowerCase())));

  return (
    <>
      {/* ── AI-svarblok: den entydige definition ─────────────────────────── */}
      <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="course-definition">
        <h2 className="text-xl font-semibold mb-3 flex items-center gap-2" style={{ color: INK }}>
          <BookOpen className="h-5 w-5" /> Hvad er {group.shortTitle || group.title}?
        </h2>
        <p className="text-slate-700 leading-relaxed text-[15px]">
          {fillTokens(facts.definition, tokens)}
        </p>
        {details.targetAudience && (
          <>
            <h3 className="mt-5 mb-1.5 text-base font-semibold text-slate-900">Hvem er kurset for?</h3>
            <p className="text-slate-600 leading-relaxed">{details.targetAudience}</p>
          </>
        )}
      </div>

      {/* ── Faktaboks i rigtig HTML (ikke grafik) ────────────────────────── */}
      <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="course-factbox">
        <h2 className="text-xl font-semibold mb-3 flex items-center gap-2" style={{ color: INK }}>
          <ShieldCheck className="h-5 w-5" /> Fakta om kurset
        </h2>
        <dl>
          <Row label="Kursus" testid="fact-name">{group.title}</Row>
          <Row label="Udbyder">{CONTACT.legalName} ({CONTACT.brandName})</Row>
          <Row label="Kursustype" testid="fact-kind">{facts.kindLabel}</Row>
          {group.selfStudy && facts.deliveryLabel && (
            <Row label="Undervisningsform" testid="fact-delivery">{facts.deliveryLabel}</Row>
          )}
          {group.duration && (
            <Row label={group.selfStudy ? "Tidsforbrug" : "Varighed"} testid="fact-duration">{group.duration}</Row>
          )}
          <Row label="Pris" testid="fact-price">
            {group.hasTypeVariants
              ? `${kr(model.low)} til ${kr(model.high)} afhængigt af modul`
              : group.hasLanguageVariants && model.high !== model.low
                ? `${kr(model.low)} på dansk · ${kr(model.high)} på fremmedsprog`
                : kr(model.low)}
            {group.amuPrice ? ` · AMU-pris ${kr(group.amuPrice)}` : ""}
            {group.examIncluded && group.examAttempts
              ? ` · prøven er inkluderet med ${group.examAttempts} forsøg`
              : ""}
          </Row>
          <Row label="Undervisningssprog" testid="fact-languages">
            {languages.length
              ? languages.join(", ")
              : group.hasLanguageVariants
                ? Object.values(LANGUAGES).map((l) => l.name).join(", ")
                : "Dansk"}
            {languages.length && group.hasLanguageVariants
              ? (group.selfStudy ? " (aktuelt udbud, se kommende prøvedatoer)" : " (aktuelt udbud, se kommende hold)")
              : ""}
          </Row>
          <Row label="Sted" testid="fact-venue">
            {(venues.length
              ? venues
              : group.location
                ? [group.location]
                : [...new Set(VOTRA_LOCATIONS.map((l) => l.city))]).join(", ")}
          </Row>
          {facts.credential && <Row label="Bevis">{facts.credential}</Row>}
          {group.examVenues?.length > 0 && (
            <Row label="Prøvested" testid="fact-exam-venue">
              Votra {group.examVenues.join(" og ")}
            </Row>
          )}
          {facts.examAuthority && <Row label="Prøve administreres af">{facts.examAuthority}</Row>}
          <Row label={group.selfStudy ? "Næste prøvedato" : "Næste hold"} testid="fact-next">
            {next ? `${next.date}${next.location ? " · " + next.location : ""}${next.languageName ? " · " + next.languageName : ""}` : (group.selfStudy ? "Se kommende prøvedatoer nedenfor" : "Se kommende hold nedenfor")}
          </Row>
        </dl>
        <p className="mt-3 text-xs text-slate-400" data-testid="course-updated">
          Sidst opdateret: {LAST_UPDATED}. Priser og datoer vedligeholdes løbende, kommende hold opdateres automatisk.
        </p>
      </div>

      {/* ── Godkendelse, prøve og ekstra omkostninger ────────────────────── */}
      <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="course-approval">
        <h2 className="text-xl font-semibold mb-3 flex items-center gap-2" style={{ color: INK }}>
          <Landmark className="h-5 w-5" /> Godkendelse, prøve og gebyrer
        </h2>
        <p className="text-slate-600 leading-relaxed">{facts.approvalNote}</p>
        {partnerVenue && (
          <p className="mt-2 text-sm text-slate-500">
            Undervisningen kan foregå hos vores samarbejdspartner UCplus. Votras egne adresser er{" "}
            {VOTRA_LOCATIONS.map((l) => `${l.city} (${l.street})`).join(", ")}.
          </p>
        )}

        {facts.extraCosts?.length > 0 && (
          <div className="mt-4" data-testid="course-extra-costs">
            <h3 className="text-base font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <Receipt className="h-4 w-4" /> Udgifter ud over kursusprisen
            </h3>
            <ul className="text-sm text-slate-600 space-y-1">
              {facts.extraCosts.map((c) => (
                <li key={c.label} className="flex justify-between gap-4 border-b border-slate-100 py-1.5">
                  <span>{c.label}</span>
                  <span className="font-medium text-slate-800">{kr(c.amount)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {facts.exam ? (
          <div className="mt-4 text-sm text-slate-600 space-y-1" data-testid="course-exam">
            {Object.entries(facts.exam).map(([k, v]) => (
              <p key={k}><span className="text-slate-500">{k}:</span> <span className="font-medium text-slate-800">{v}</span></p>
            ))}
            {facts.examNote && (
              <p className="pt-2 text-slate-500" data-testid="course-exam-note">{facts.examNote}</p>
            )}
          </div>
        ) : facts.examAuthority ? (
          <p className="mt-4 text-sm text-slate-600" data-testid="course-exam-pending">
            Prøveform, antal spørgsmål, beståelseskrav og det aktuelle gebyr fastsættes af {facts.examAuthority}.
            Vi oplyser de gældende vilkår ved tilmelding og hjælper dig med at blive tilmeldt prøven.
            ring på <a href={`tel:${CONTACT.phone}`} className="font-medium hover:underline" style={{ color: BRAND }}>{CONTACT.phoneDisplay}</a>.
          </p>
        ) : null}

        {facts.authorities?.length > 0 && (
          <div className="mt-5" data-testid="course-authorities">
            <h3 className="text-base font-semibold text-slate-900 mb-2">Officielle kilder</h3>
            <ul className="space-y-1.5">
              {facts.authorities.map((a) => (
                <li key={a.url}>
                  <a href={a.url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm hover:underline" style={{ color: BRAND }}>
                    {a.label} <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

/** Samlet FAQ: kursusspecifikke spørgsmål først, derefter de generelle. */
export const courseFaqList = (group, sessions, generalFaq = []) => {
  const { facts, tokens } = buildCourseFactModel(group, sessions);
  const own = (facts?.faq || []).map((f) => ({
    question: fillTokens(f.q, tokens),
    answer: fillTokens(f.a, tokens),
  }));
  const seen = new Set(own.map((f) => f.question.toLowerCase()));
  return [...own, ...generalFaq.filter((f) => !seen.has((f.question || "").toLowerCase()))];
};

/**
 * Én samlet JSON-LD-blok pr. kursusside: Course med pris, varighed, sprog, bevis,
 * adgangskrav og hvert kommende hold som CourseInstance — plus ÉN FAQPage.
 */
export const CourseStructuredData = ({ group, sessions, faq }) => {
  const { facts, low, high, upcoming, tokens } = buildCourseFactModel(group, sessions);
  const path = `/kurser/${group.slug}`;
  const details = COURSE_DETAILS[group.slug] || {};

  const instances = upcoming.slice(0, 15).map((s) => {
    const venue = resolveVenue(s.location);
    return {
      "@type": "CourseInstance",
      name: group.selfStudy
        ? `${group.title}, prøvedato ${s.date}`
        : `${group.title}, start ${s.date}`,
      startDate: s.date,
      // Et selvstudieforløb har en prøvedato, ikke en periode. `end_date` på holdet
      // dækker administrationens vindue, og uden dette så schemaet ud, som om
      // prøven varede to uger, i modstrid med den synlige «prøvedato».
      ...(s.end_date && !group.selfStudy ? { endDate: s.end_date } : {}),
      ...(group.selfStudy ? { endDate: s.date } : {}),
      courseMode: "onsite",
      ...(s.languageCode || s.language ? { inLanguage: s.languageCode || s.language } : {}),
      location: {
        "@type": "Place",
        name: venue ? venue.name : s.location || "Votra Danmark ApS",
        ...(venue
          ? {
            address: {
              "@type": "PostalAddress",
              streetAddress: venue.street,
              postalCode: venue.postalCode,
              addressLocality: venue.city,
              addressCountry: "DK",
            },
          }
          : { address: { "@type": "PostalAddress", addressLocality: s.location, addressCountry: "DK" } }),
      },
      ...(typeof s.price === "number" && s.price > 0
        ? {
          offers: {
            "@type": "Offer",
            price: s.price,
            priceCurrency: "DKK",
            url: `${SITE_URL}${path}`,
            availability: (s.total_spots - s.booked_spots) > 0
              ? "https://schema.org/InStock"
              : "https://schema.org/SoldOut",
          },
        }
        : {}),
      ...(typeof s.total_spots === "number" ? { maximumAttendeeCapacity: s.total_spots } : {}),
      ...(typeof s.total_spots === "number" && typeof s.booked_spots === "number"
        ? { remainingAttendeeCapacity: Math.max(0, s.total_spots - s.booked_spots) }
        : {}),
    };
  });

  const prerequisites = [
    ...(group.requirements || []),
    ...(group.requirementsNote ? [group.requirementsNote] : []),
  ];

  const blocks = [
    courseJsonLd({
      name: group.title,
      description: fillTokens(facts?.definition || group.description, tokens),
      path,
      priceLow: low,
      priceHigh: high,
      duration: group.duration,
      languages: [...new Set(upcoming.map((s) => s.languageCode || s.language).filter(Boolean))],
      credential: facts?.credential,
      prerequisites,
      audience: details.targetAudience,
      teaches: details.learningOutcomes || group.content,
      courseMode: group.selfStudy ? ["online", "onsite"] : undefined,
      instances,
    }),
  ];
  if (faq?.length) {
    blocks.push(faqJsonLd(faq.map((f) => ({ q: f.question, a: f.answer })), `${SITE_URL}${path}#faq`));
  }

  return (
    <Helmet>
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </Helmet>
  );
};

export default CourseFactsSections;
